import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppFileUploadModel, ErrorResponse } from '@app/models';
import { environment } from '@env/environment';
import { Observable, switchMap, tap } from 'rxjs';

import { downloadBlob } from 'src/assets/helpers/downloadFile';

/**
 * macOS screenshot names use U+202F (narrow no-break space) before AM/PM, which can break server-side
 * confirm / path logic. Normalize problematic Unicode spaces for API payloads.
 */
export function sanitizeUploadFileName(fileName: string): string {
  if (!fileName) return fileName;
  return fileName
    .replace(/\u202F/g, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/\u2007/g, ' ')
    .replace(/\uFEFF/g, '');
}

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  generatedId = 0;

  constructor(private http: HttpClient) {}

  uploadVendorAppFiles(appFileUploadModel: AppFileUploadModel): Observable<AppFileUploadModel> {
    return this.http.post<AppFileUploadModel>(
      `${environment.apiUrl}/api/vendor/Application/SaveApplicationFiles`,
      appFileUploadModel
    );
  }

  uploadAgencyAppFiles(appFileUploadModel: AppFileUploadModel): Observable<AppFileUploadModel> {
    return this.http.post<AppFileUploadModel>(
      `${environment.apiUrl}/api/agency/Application/SaveApplicationFiles`,
      appFileUploadModel
    );
  }

  uploadAWSFile(uploadUrl: string, file: File): Observable<any> {
    const headers = {
      'Content-Type': this.correctFileType(file.type),
    };

    return this.http.put<any>(uploadUrl, file, {
      headers: headers,
      reportProgress: true,
      observe: 'events',
      // context: new HttpContext().set(NO_AUTH, true)
    });
  }

  confirmVendorAppFileUpload(applicationId: number, fileIdentifier: string): Observable<ErrorResponse> {
    return this.http.post<ErrorResponse>(
      `${environment.apiUrl}/api/vendor/Application/ConfirmAppFileUpload`,
      { applicationId, fileIdentifier },
      {}
    );
  }

  confirmAgencyAppFileUpload(applicationId: number, fileIdentifier: string): Observable<ErrorResponse> {
    return this.http.post<ErrorResponse>(
      `${environment.apiUrl}/api/agency/Application/ConfirmAppFileUpload`,
      { applicationId, fileIdentifier },
      {}
    );
  }

  downloadAppFile(applicationId: number, identifier: string, fileName: string): Observable<HttpResponse<any>> {
    return this.http
      .post<string>(`${environment.apiUrl}/api/general/Base/AccessAndDownloadFileLink`, { applicationId, identifier })
      .pipe(
        switchMap(fileUrl => {
          return this.http.get(fileUrl, {
            responseType: 'blob',
            observe: 'response',
          });
        }),
        tap(downloadBlob(fileName))
      );
  }

  removeVendorAppFile(applicationId: number, identifier: string): Observable<ErrorResponse> {
    return this.http.post<ErrorResponse>(`${environment.apiUrl}/api/vendor/Application/RemoveApplicationFile`, {
      applicationId,
      identifier,
    });
  }

  removeAgencyAppFile(applicationId: number, identifier: string): Observable<ErrorResponse> {
    return this.http.post<ErrorResponse>(`${environment.apiUrl}/api/agency/Application/RemoveApplicationFile`, {
      applicationId,
      identifier,
    });
  }

  async computeChecksumMd5(file: File): Promise<string> {
    // Dynamically import spark-md5 only when needed
    const SparkMD5 = await import('spark-md5');

    return new Promise((resolve, reject) => {
      const chunkSize = 2097152; // Read in chunks of 2MB
      const spark = new SparkMD5.default.ArrayBuffer();
      const fileReader = new FileReader();

      let cursor = 0; // current cursor in file

      fileReader.onerror = function (): void {
        reject('MD5 computation failed - error reading the file');
      };

      // read chunk starting at `cursor` into memory
      function processChunk(chunk_start: number): void {
        const chunk_end = Math.min(file.size, chunk_start + chunkSize);
        fileReader.readAsArrayBuffer(file.slice(chunk_start, chunk_end));
      }

      fileReader.onload = function (e: any): void {
        spark.append(e.target.result); // Accumulate chunk to md5 computation
        cursor += chunkSize; // Move past this chunk

        if (cursor < file.size) {
          // Enqueue next chunk to be accumulated
          processChunk(cursor);
        } else {
          // Computation ended, last chunk has been processed. Return as Promise value.
          // This returns the base64 encoded md5 hash
          // resolve(btoa(spark.end(true)));
          // This returns the hexdigest
          resolve(spark.end());
        }
      };

      processChunk(0);
    });
  }

  private correctFileType(type: string): string {
    if (type == 'application/vnd.ms-excel') {
      return 'text/csv';
    }
    if (type == 'application/vnd.ms-publisher') {
      return 'application/x-mspublisher';
    }
    return type;
  }
}
